import React, { useState, useEffect, useCallback, useMemo } from "react";
import dayjs from "dayjs";
import { motion, AnimatePresence } from "framer-motion";
import { Icon } from "./Icons";

const token = () => sessionStorage.getItem("jwtToken");
const authHdr = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${token()}`,
});

/**
 * Calculates current, next, and historical quarters.
 */
const getQuarterOptions = () => {
  const now = dayjs();
  const currentYear = now.year();
  const currentMonth = now.month(); // 0 - 11
  const currentQ = Math.floor(currentMonth / 3) + 1; // 1 - 4

  const nextQ = currentQ === 4 ? 1 : currentQ + 1;
  const nextQYear = currentQ === 4 ? currentYear + 1 : currentYear;

  const quarters = [
    { key: "ALL", label: "All Quarters" },
    { key: `Q${nextQ} ${nextQYear}`, label: `Q${nextQ} ${nextQYear} (Next Quarter)`, isNext: true },
    { key: `Q${currentQ} ${currentYear}`, label: `Q${currentQ} ${currentYear} (Current Quarter)`, isCurrent: true },
  ];

  // Add future quarters
  for (let i = 1; i <= 2; i++) {
    let q = nextQ + i;
    let y = nextQYear;
    while (q > 4) { q -= 4; y += 1; }
    quarters.push({ key: `Q${q} ${y}`, label: `Q${q} ${y}` });
  }

  // Add past quarters
  for (let i = 1; i <= 3; i++) {
    let q = currentQ - i;
    let y = currentYear;
    while (q < 1) { q += 4; y -= 1; }
    quarters.push({ key: `Q${q} ${y}`, label: `Q${q} ${y}` });
  }

  const seen = new Set();
  return quarters.filter(q => {
    if (seen.has(q.key)) return false;
    seen.add(q.key);
    return true;
  });
};

const WORK_TYPE_THEMES = {
  BUSINESS_WORK: { bg: "#EFF6FF", text: "#2563EB", border: "#DBEAFE", label: "Business Work", dot: "#3B82F6" },
  UPSKILLING:    { bg: "#FAF5FF", text: "#7C3AED", border: "#E9D5FF", label: "Upskilling", dot: "#8B5CF6" },
};

const getWorkTypeTheme = (val) => {
  if (!val) return WORK_TYPE_THEMES.BUSINESS_WORK;
  const upper = String(val).toUpperCase();
  if (upper.includes("UPSKILL")) {
    return WORK_TYPE_THEMES.UPSKILLING;
  }
  return WORK_TYPE_THEMES.BUSINESS_WORK;
};

const STATUS_THEMES = {
  ASSIGNED:          { bg: "#EFF6FF", text: "#2563EB", border: "#DBEAFE", label: "Assigned" },
  IN_PROGRESS:       { bg: "#FFFBEB", text: "#D97706", border: "#FDE68A", label: "In Progress" },
  COMPLETED:         { bg: "#ECFDF5", text: "#059669", border: "#A7F3D0", label: "Completed" },
  REVIEWED:          { bg: "#F5F3FF", text: "#7C3AED", border: "#DDD6FE", label: "Appraised" },
  NEEDS_IMPROVEMENT: { bg: "#FEF2F2", text: "#DC2626", border: "#FECACA", label: "Needs Improvement" },
};

const RATING_DESCRIPTIONS = {
  1: "1 - Needs Improvement",
  2: "2 - Below Expectations",
  3: "3 - Meets Expectations",
  4: "4 - Exceeds Expectations",
  5: "5 - Outstanding",
};

export const EmployeeTargetsAppraisalsView = ({ username, name }) => {
  const quarterOptions = useMemo(() => getQuarterOptions(), []);
  // Quarters list without 'ALL' for modals
  const quarterSelectOptions = useMemo(() => quarterOptions.filter(q => q.key !== "ALL"), [quarterOptions]);

  const [selectedQuarter, setSelectedQuarter] = useState("ALL");
  const [selectedClient, setSelectedClient] = useState("ALL");
  const [selectedProject, setSelectedProject] = useState("ALL");
  const [selectedStatus, setSelectedStatus] = useState("ALL");
  const [selectedWorkType, setSelectedWorkType] = useState("ALL");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("NEWEST");

  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [message, setMessage] = useState(null);

  // Modals state
  const [detailTarget, setDetailTarget] = useState(null);
  const [taskModal, setTaskModal] = useState({ isOpen: false, target: null });
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, targetId: null, title: "" });

  // Add / Edit Task Form
  const [taskForm, setTaskForm] = useState({
    id: null,
    quarter: "",
    title: "",
    description: "",
    kpi: "",
    workType: "BUSINESS_WORK",
    status: "ASSIGNED",
    dueDate: "",
  });
  const [submitting, setSubmitting] = useState(false);

  // Fetch employee targets
  const fetchTargets = useCallback(async (isSilent = false) => {
    if (!username) return;
    if (!isSilent) setLoading(true);
    else setRefreshing(true);

    try {
      const res = await fetch(`/timesheets/appraisals/employee/${encodeURIComponent(username)}`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (res.ok) {
        const data = await res.json();
        setTargets(Array.isArray(data) ? data : []);
      } else {
        setTargets([]);
      }
    } catch (e) {
      console.error("Error fetching employee targets:", e);
      setTargets([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [username]);

  useEffect(() => {
    fetchTargets();
  }, [fetchTargets]);

  // Auto-dismiss alert popup after 3.5 seconds
  useEffect(() => {
    if (!message) return;
    const timer = setTimeout(() => {
      setMessage(null);
    }, 3500);
    return () => clearTimeout(timer);
  }, [message]);

  // Open Add / Edit Modal
  const openTaskModal = (target = null) => {
    if (target) {
      setTaskForm({
        id: target.id,
        quarter: target.quarter || quarterSelectOptions[0]?.key || "",
        title: target.title || "",
        description: target.description || "",
        kpi: target.kpi || "",
        workType: (target.workType || target.priority || "").toUpperCase().includes("UPSKILL") ? "UPSKILLING" : "BUSINESS_WORK",
        status: target.status || "ASSIGNED",
        dueDate: target.dueDate || "",
      });
    } else {
      const defaultQ = (selectedQuarter !== "ALL" && selectedQuarter)
        ? selectedQuarter
        : (quarterSelectOptions.find(q => q.isCurrent)?.key || quarterSelectOptions[0]?.key || "");
      setTaskForm({
        id: null,
        quarter: defaultQ,
        title: "",
        description: "",
        kpi: "",
        workType: selectedWorkType !== "ALL" ? selectedWorkType : "BUSINESS_WORK",
        status: "ASSIGNED",
        dueDate: "",
      });
    }
    setTaskModal({ isOpen: true, target });
  };

  // Save Task (Create or Update)
  const handleSaveTask = async (e) => {
    e.preventDefault();
    const isUpskilling = (taskForm.workType || "").toUpperCase().includes("UPSKILL");

    if (!taskForm.title.trim() && !isUpskilling) {
      setMessage({ type: "error", text: "Please enter a target title / project." });
      return;
    }

    if (isUpskilling && !taskForm.title.trim() && !taskForm.description.trim()) {
      setMessage({ type: "error", text: "Please enter a description of the skills you are learning." });
      return;
    }

    setSubmitting(true);
    setMessage(null);

    const isEdit = !!taskForm.id;
    const url = isEdit ? `/timesheets/appraisals/${taskForm.id}` : "/timesheets/appraisals/assign";
    const method = isEdit ? "PUT" : "POST";

    const resolvedTitle = taskForm.title.trim()
      || (taskForm.description.trim()
        ? (taskForm.description.trim().length > 60 ? taskForm.description.trim().slice(0, 57) + "..." : taskForm.description.trim())
        : "Upskilling & Learning");

    try {
      const res = await fetch(url, {
        method,
        headers: authHdr(),
        body: JSON.stringify({
          quarter: taskForm.quarter,
          title: resolvedTitle,
          description: taskForm.description.trim(),
          kpi: taskForm.kpi.trim(),
          workType: taskForm.workType || "BUSINESS_WORK",
          priority: taskForm.workType || "BUSINESS_WORK",
          status: taskForm.status,
          dueDate: taskForm.dueDate || null,
        }),
      });

      if (res.ok) {
        const saved = await res.json();
        setTargets(prev => {
          if (isEdit) {
            return prev.map(t => (t.id === saved.id ? saved : t));
          }
          return [saved, ...prev];
        });

        if (detailTarget && detailTarget.id === saved.id) {
          setDetailTarget(saved);
        }

        setTaskModal({ isOpen: false, target: null });
        setMessage({
          type: "success",
          text: isEdit ? "Task updated successfully!" : "Task added successfully! It is now visible to you and your employer.",
        });
      } else {
        const errText = await res.text();
        setMessage({ type: "error", text: errText || "Failed to save task." });
      }
    } catch (e) {
      console.error("Error saving task:", e);
      setMessage({ type: "error", text: "Network error saving task." });
    } finally {
      setSubmitting(false);
    }
  };

  // Delete Task
  const handleDeleteTask = async () => {
    if (!deleteModal.targetId) return;
    setSubmitting(true);
    try {
      const res = await fetch(`/timesheets/appraisals/${deleteModal.targetId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token()}` },
      });

      if (res.ok) {
        setTargets(prev => prev.filter(t => t.id !== deleteModal.targetId));
        if (detailTarget && detailTarget.id === deleteModal.targetId) {
          setDetailTarget(null);
        }
        setDeleteModal({ isOpen: false, targetId: null, title: "" });
        setMessage({ type: "success", text: "Task deleted successfully." });
      } else {
        const errText = await res.text();
        setMessage({ type: "error", text: errText || "Failed to delete task." });
      }
    } catch (e) {
      console.error("Error deleting task:", e);
      setMessage({ type: "error", text: "Network error deleting task." });
    } finally {
      setSubmitting(false);
    }
  };

  // Update target status by employee
  const handleUpdateStatus = async (targetId, newStatus) => {
    setUpdatingId(targetId);
    try {
      const res = await fetch(`/timesheets/appraisals/${targetId}/status`, {
        method: "PATCH",
        headers: authHdr(),
        body: JSON.stringify({ status: newStatus }),
      });

      if (res.ok) {
        const updatedTarget = await res.json();
        setTargets(prev => prev.map(t => (t.id === targetId ? updatedTarget : t)));
        if (detailTarget && detailTarget.id === targetId) {
          setDetailTarget(updatedTarget);
        }
        setMessage({
          type: "success",
          text: newStatus === "COMPLETED"
            ? "Task marked as Completed! Your employer has been notified to review and provide an appraisal."
            : `Task status updated to "${STATUS_THEMES[newStatus]?.label || newStatus}"`,
        });
      } else {
        const errText = await res.text();
        setMessage({
          type: "error",
          text: errText || "Failed to update task status.",
        });
      }
    } catch (e) {
      console.error("Error updating status:", e);
      setMessage({ type: "error", text: "Network error updating task status." });
    } finally {
      setUpdatingId(null);
    }
  };

  // Available distinct project titles for employee
  const projectOptions = useMemo(() => {
    const set = new Set();
    targets.forEach(t => {
      if (t.title && t.title.trim()) set.add(t.title.trim());
    });
    return Array.from(set);
  }, [targets]);

  // Available distinct clients for employee
  const clientOptions = useMemo(() => {
    const set = new Set();
    targets.forEach(t => {
      if (t.clientName && t.clientName.trim()) set.add(t.clientName.trim());
    });
    return Array.from(set);
  }, [targets]);

  // Filter and Sort targets
  const filteredTargets = useMemo(() => {
    let result = targets.filter(t => {
      // Quarter filter
      if (selectedQuarter !== "ALL" && t.quarter !== selectedQuarter) {
        return false;
      }
      // Client filter
      if (selectedClient !== "ALL") {
        if ((t.clientName || "").toLowerCase() !== selectedClient.toLowerCase()) {
          return false;
        }
      }
      // Project filter
      if (selectedProject !== "ALL") {
        if ((t.title || "").trim().toLowerCase() !== selectedProject.toLowerCase()) {
          return false;
        }
      }
      // Status filter
      if (selectedStatus !== "ALL" && t.status !== selectedStatus) {
        return false;
      }
      // Work Type filter
      if (selectedWorkType !== "ALL") {
        const itemType = (t.workType || t.priority || "").toUpperCase().includes("UPSKILL") ? "UPSKILLING" : "BUSINESS_WORK";
        if (itemType !== selectedWorkType) {
          return false;
        }
      }
      // Search query
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchesTitle = (t.title || "").toLowerCase().includes(q);
        const matchesClient = (t.clientName || "").toLowerCase().includes(q);
        const matchesDesc = (t.description || "").toLowerCase().includes(q);
        const matchesKpi = (t.kpi || "").toLowerCase().includes(q);
        const matchesAssigned = (t.assignedBy || "").toLowerCase().includes(q);
        const matchesQuarter = (t.quarter || "").toLowerCase().includes(q);
        if (!matchesTitle && !matchesClient && !matchesDesc && !matchesKpi && !matchesAssigned && !matchesQuarter) {
          return false;
        }
      }
      return true;
    });

    // Sorting
    result.sort((a, b) => {
      if (sortBy === "DUE_DATE_ASC") {
        if (!a.dueDate) return 1;
        if (!b.dueDate) return -1;
        return a.dueDate.localeCompare(b.dueDate);
      }
      if (sortBy === "DUE_DATE_DESC") {
        if (!a.dueDate) return 1;
        if (!b.dueDate) return -1;
        return b.dueDate.localeCompare(a.dueDate);
      }
      if (sortBy === "RATING_DESC") {
        return (b.rating || 0) - (a.rating || 0);
      }
      if (sortBy === "WORK_TYPE") {
        const aType = (a.workType || a.priority || "").toUpperCase().includes("UPSKILL") ? "UPSKILLING" : "BUSINESS_WORK";
        const bType = (b.workType || b.priority || "").toUpperCase().includes("UPSKILL") ? "UPSKILLING" : "BUSINESS_WORK";
        return aType.localeCompare(bType);
      }
      if (sortBy === "NEWEST") {
        const timeA = a.assignedAt ? new Date(a.assignedAt).getTime() : 0;
        const timeB = b.assignedAt ? new Date(b.assignedAt).getTime() : 0;
        if (timeB !== timeA) return timeB - timeA;
        return (Number(b.id) || 0) - (Number(a.id) || 0);
      }
      return 0;
    });

    return result;
  }, [targets, selectedQuarter, selectedClient, selectedProject, selectedStatus, selectedWorkType, searchQuery, sortBy]);

  // Metric computations
  const totalCount = targets.length;
  const inProgressCount = targets.filter(t => t.status === "IN_PROGRESS").length;
  const completedCount = targets.filter(t => t.status === "COMPLETED" || t.status === "REVIEWED").length;
  const reviewedTargets = targets.filter(t => t.rating > 0 || t.status === "REVIEWED");
  const ratedCount = reviewedTargets.length;
  const avgRating = ratedCount > 0
    ? (reviewedTargets.reduce((sum, t) => sum + (t.rating || 0), 0) / ratedCount).toFixed(1)
    : null;

  // Render Stars Helper
  const renderStarIcons = (rating = 0, size = 15) => {
    return (
      <div style={{ display: "inline-flex", gap: 2.5, alignItems: "center" }}>
        {[1, 2, 3, 4, 5].map(star => {
          const filled = star <= rating;
          return (
            <span key={star} style={{ color: filled ? "#F59E0B" : "#CBD5E1", display: "inline-flex" }}>
              <Icon.Star size={size} color={filled ? "#F59E0B" : "#CBD5E1"} />
            </span>
          );
        })}
      </div>
    );
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20, width: "100%", paddingBottom: 40 }}>
      {/* ── HEADER BANNER ── */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        style={{
          background: "linear-gradient(135deg, #FFFFFF 0%, #F4F8FF 100%)",
          borderRadius: 16,
          border: "1px solid #E2EEFF",
          padding: "22px 26px",
          boxShadow: "0 2px 10px rgba(37,99,235,0.06)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  background: "#EFF6FF",
                  border: "1px solid #DBEAFE",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#2563EB",
                }}
              >
                <Icon.Target size={20} color="#2563EB" />
              </div>
              <h2 style={{ fontSize: "1.25rem", fontWeight: 800, color: "#0F172A", margin: 0, letterSpacing: "-0.02em" }}>
                My Tasks & Targets
              </h2>
            </div>
            <p style={{ fontSize: "0.85rem", color: "#64748B", margin: 0, maxWidth: 640, lineHeight: 1.5 }}>
              Add and manage your tasks, track work deliverables, and view employer appraisal ratings and feedback after completing tasks.
            </p>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            {/* Add Task Button */}
            <button
              type="button"
              onClick={() => openTaskModal(null)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "9px 18px",
                borderRadius: 10,
                border: "none",
                background: "#2563EB",
                color: "#fff",
                fontSize: "0.84rem",
                fontWeight: 700,
                cursor: "pointer",
                boxShadow: "0 2px 8px rgba(37,99,235,0.28)",
                transition: "all 0.15s ease",
              }}
            >
              <Icon.Plus size={15} color="#fff" />
              <span>Add Task</span>
            </button>

            {/* Refresh Button */}
            <button
              type="button"
              onClick={() => fetchTargets(true)}
              disabled={refreshing}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 14px",
                borderRadius: 10,
                background: "#fff",
                border: "1.5px solid #E2EEFF",
                color: "#2563EB",
                fontSize: "0.8rem",
                fontWeight: 600,
                cursor: "pointer",
                boxShadow: "0 1px 3px rgba(37,99,235,0.04)",
                transition: "all 0.15s ease",
              }}
              title="Refresh tasks & targets"
            >
              <span style={{ display: "inline-block", transform: refreshing ? "rotate(180deg)" : "none", transition: "transform 0.4s ease" }}>
                <Icon.Clock size={13} color="#2563EB" />
              </span>
              {refreshing ? "Refreshing..." : "Refresh"}
            </button>
          </div>
        </div>

        {/* ── METRICS TILES ── */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
            gap: 12,
            marginTop: 20,
          }}
        >
          {/* Tile 1: Total Tasks */}
          <div style={{ background: "#fff", padding: "14px 18px", borderRadius: 12, border: "1px solid #E2EEFF", boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: "0.72rem", fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.05em" }}>Total Tasks</span>
              <span style={{ color: "#2563EB" }}><Icon.Target size={15} /></span>
            </div>
            <div style={{ fontSize: "1.45rem", fontWeight: 800, color: "#0F172A", marginTop: 6 }}>{totalCount}</div>
            <div style={{ fontSize: "0.72rem", color: "#94A3B8", marginTop: 2 }}>Assigned & self-added</div>
          </div>

          {/* Tile 2: In Progress */}
          <div style={{ background: "#fff", padding: "14px 18px", borderRadius: 12, border: "1px solid #E2EEFF", boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: "0.72rem", fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.05em" }}>In Progress</span>
              <span style={{ color: "#D97706" }}><Icon.Clock size={15} /></span>
            </div>
            <div style={{ fontSize: "1.45rem", fontWeight: 800, color: "#D97706", marginTop: 6 }}>{inProgressCount}</div>
            <div style={{ fontSize: "0.72rem", color: "#94A3B8", marginTop: 2 }}>Currently active goals</div>
          </div>

          {/* Tile 3: Completed */}
          <div style={{ background: "#fff", padding: "14px 18px", borderRadius: 12, border: "1px solid #E2EEFF", boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: "0.72rem", fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.05em" }}>Completed</span>
              <span style={{ color: "#059669" }}><Icon.CheckCircle size={15} /></span>
            </div>
            <div style={{ fontSize: "1.45rem", fontWeight: 800, color: "#059669", marginTop: 6 }}>{completedCount}</div>
            <div style={{ fontSize: "0.72rem", color: "#94A3B8", marginTop: 2 }}>Finished deliverables</div>
          </div>

          {/* Tile 4: Appraised / Reviewed */}
          <div style={{ background: "#fff", padding: "14px 18px", borderRadius: 12, border: "1px solid #E2EEFF", boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: "0.72rem", fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.05em" }}>Appraised</span>
              <span style={{ color: "#7C3AED" }}><Icon.Award size={15} /></span>
            </div>
            <div style={{ fontSize: "1.45rem", fontWeight: 800, color: "#7C3AED", marginTop: 6 }}>{ratedCount}</div>
            <div style={{ fontSize: "0.72rem", color: "#94A3B8", marginTop: 2 }}>Reviewed by employer</div>
          </div>

          {/* Tile 5: Average Rating */}
          <div
            style={{
              background: avgRating ? "linear-gradient(135deg, #FEFCE8 0%, #FFFBEB 100%)" : "#fff",
              padding: "14px 18px",
              borderRadius: 12,
              border: `1px solid ${avgRating ? "#FDE68A" : "#E2EEFF"}`,
              boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: "0.72rem", fontWeight: 700, color: avgRating ? "#B45309" : "#64748B", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                Avg Appraisal Rating
              </span>
              <span style={{ color: "#F59E0B" }}><Icon.Star size={15} /></span>
            </div>
            <div style={{ fontSize: "1.45rem", fontWeight: 800, color: avgRating ? "#B45309" : "#0F172A", marginTop: 6, display: "flex", alignItems: "baseline", gap: 6 }}>
              {avgRating ? `${avgRating}` : "—"}
              {avgRating && <span style={{ fontSize: "0.75rem", fontWeight: 600, color: "#D97706" }}>/ 5.0</span>}
            </div>
            <div style={{ fontSize: "0.72rem", color: avgRating ? "#D97706" : "#94A3B8", marginTop: 2, fontWeight: 500 }}>
              {avgRating ? "⭐ Overall performance" : "Awaiting appraisals"}
            </div>
          </div>
        </div>
      </motion.div>

      {/* ── NOTIFICATION MESSAGE BANNER ── */}
      <AnimatePresence>
        {message && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            style={{
              padding: "12px 18px",
              borderRadius: 10,
              fontSize: "0.84rem",
              fontWeight: 600,
              background: message.type === "success" ? "#ECFDF5" : "#FEF2F2",
              color: message.type === "success" ? "#059669" : "#DC2626",
              border: `1px solid ${message.type === "success" ? "#A7F3D0" : "#FECACA"}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              boxShadow: "0 2px 6px rgba(0,0,0,0.03)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {message.type === "success" ? <Icon.CheckCircle size={16} color="#059669" /> : <Icon.AlertCircle size={16} color="#DC2626" />}
              <span>{message.text}</span>
            </div>
            <button
              onClick={() => setMessage(null)}
              style={{ background: "none", border: "none", cursor: "pointer", color: "inherit", fontWeight: 700, fontSize: "0.9rem" }}
            >
              ✕
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── SEARCH & FILTER CONTROLS ── */}
      <div
        style={{
          background: "#fff",
          border: "1px solid #E2EEFF",
          borderRadius: 14,
          padding: "16px 20px",
          display: "flex",
          flexDirection: "column",
          gap: 14,
          boxShadow: "0 1px 4px rgba(37,99,235,0.03)",
        }}
      >
        {/* Row 1: Search & Dropdowns */}
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", justifyContent: "space-between" }}>
          {/* Search Box */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 14px",
              background: "#F8FAFC",
              border: "1.5px solid #E2E8F0",
              borderRadius: 10,
              minWidth: 260,
              flex: "1 1 260px",
            }}
          >
            <Icon.Search size={14} color="#94A3B8" />
            <input
              type="text"
              placeholder="Search task title, description, KPI, or creator..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              style={{ border: "none", outline: "none", fontSize: "0.82rem", color: "#0F172A", width: "100%", background: "transparent" }}
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                style={{ background: "none", border: "none", cursor: "pointer", color: "#94A3B8", fontSize: "0.75rem" }}
              >
                ✕
              </button>
            )}
          </div>

          {/* Quarter Select */}
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: "0.75rem", fontWeight: 700, color: "#64748B", textTransform: "uppercase" }}>Quarter:</span>
            <select
              value={selectedQuarter}
              onChange={e => setSelectedQuarter(e.target.value)}
              style={{
                padding: "8px 12px",
                borderRadius: 8,
                border: "1.5px solid #E2EEFF",
                fontSize: "0.82rem",
                color: "#1E293B",
                fontWeight: 600,
                background: "#fff",
                outline: "none",
                cursor: "pointer",
              }}
            >
              {quarterOptions.map(q => (
                <option key={q.key} value={q.key}>{q.label}</option>
              ))}
            </select>
          </div>

          {/* Client Select */}
          {clientOptions.length > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: "0.75rem", fontWeight: 700, color: "#64748B", textTransform: "uppercase" }}>Client:</span>
              <select
                value={selectedClient}
                onChange={e => setSelectedClient(e.target.value)}
                style={{
                  padding: "8px 12px",
                  borderRadius: 8,
                  border: "1.5px solid #E2EEFF",
                  fontSize: "0.82rem",
                  color: "#1E293B",
                  fontWeight: 600,
                  background: "#fff",
                  outline: "none",
                  cursor: "pointer",
                }}
                title="Filter by Client"
              >
                <option value="ALL">All Clients</option>
                {clientOptions.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          )}

          {/* Project Select */}
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: "0.75rem", fontWeight: 700, color: "#64748B", textTransform: "uppercase" }}>Project:</span>
            <select
              value={selectedProject}
              onChange={e => setSelectedProject(e.target.value)}
              style={{
                padding: "8px 12px",
                borderRadius: 8,
                border: "1.5px solid #E2EEFF",
                fontSize: "0.82rem",
                color: "#1E293B",
                fontWeight: 600,
                background: "#fff",
                outline: "none",
                cursor: "pointer",
              }}
              title="Filter by Project"
            >
              <option value="ALL">All Projects</option>
              {projectOptions.map(p => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>

          {/* Work Type Select */}
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: "0.75rem", fontWeight: 700, color: "#64748B", textTransform: "uppercase" }}>Work Type:</span>
            <select
              value={selectedWorkType}
              onChange={e => setSelectedWorkType(e.target.value)}
              style={{
                padding: "8px 12px",
                borderRadius: 8,
                border: "1.5px solid #E2EEFF",
                fontSize: "0.82rem",
                color: "#1E293B",
                fontWeight: 600,
                background: "#fff",
                outline: "none",
                cursor: "pointer",
              }}
            >
              <option value="ALL">All Work Types</option>
              <option value="BUSINESS_WORK">💼 Business Work</option>
              <option value="UPSKILLING">🚀 Upskilling</option>
            </select>
          </div>

          {/* Sort By */}
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: "0.75rem", fontWeight: 700, color: "#64748B", textTransform: "uppercase" }}>Sort:</span>
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              style={{
                padding: "8px 12px",
                borderRadius: 8,
                border: "1.5px solid #E2EEFF",
                fontSize: "0.82rem",
                color: "#1E293B",
                fontWeight: 600,
                background: "#fff",
                outline: "none",
                cursor: "pointer",
              }}
            >
              <option value="NEWEST">Recently Added</option>
              <option value="DUE_DATE_ASC">Due Date (Earliest First)</option>
              <option value="DUE_DATE_DESC">Due Date (Latest First)</option>
              <option value="WORK_TYPE">Work Type</option>
              <option value="RATING_DESC">Appraisal Rating (Highest)</option>
            </select>
          </div>
        </div>

        {/* Row 2: Status Filter Tabs */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", borderTop: "1px solid #F1F5F9", paddingTop: 12 }}>
          <span style={{ fontSize: "0.72rem", fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.05em", marginRight: 4 }}>
            Status:
          </span>
          {[
            { key: "ALL", label: "All", count: targets.length },
            { key: "ASSIGNED", label: "Assigned", count: targets.filter(t => t.status === "ASSIGNED").length },
            { key: "IN_PROGRESS", label: "In Progress", count: inProgressCount },
            { key: "COMPLETED", label: "Completed", count: targets.filter(t => t.status === "COMPLETED").length },
            { key: "REVIEWED", label: "Appraised", count: ratedCount },
          ].map(tab => {
            const isActive = selectedStatus === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setSelectedStatus(tab.key)}
                style={{
                  padding: "5px 12px",
                  borderRadius: 20,
                  border: `1px solid ${isActive ? "#2563EB" : "#E2E8F0"}`,
                  background: isActive ? "#EFF6FF" : "#fff",
                  color: isActive ? "#2563EB" : "#64748B",
                  fontSize: "0.76rem",
                  fontWeight: isActive ? 700 : 500,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  transition: "all 0.15s ease",
                }}
              >
                <span>{tab.label}</span>
                <span
                  style={{
                    background: isActive ? "#2563EB" : "#F1F5F9",
                    color: isActive ? "#fff" : "#64748B",
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    padding: "1px 6px",
                    borderRadius: 999,
                  }}
                >
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── CONTENT AREA: TABLE VIEW ── */}
      {loading ? (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "60px 20px", gap: 12, background: "#fff", borderRadius: 16, border: "1px solid #E2EEFF" }}>
          <div style={{ width: 36, height: 36, borderRadius: "50%", border: "3px solid #DBEAFE", borderTopColor: "#2563EB", animation: "spin 0.8s linear infinite" }} />
          <p style={{ fontSize: "0.85rem", color: "#64748B", fontWeight: 500, margin: 0 }}>Loading your tasks & targets...</p>
          <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        </div>
      ) : filteredTargets.length === 0 ? (
        /* Empty State */
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          style={{
            background: "#fff",
            borderRadius: 16,
            border: "1px dashed #CBD5E1",
            padding: "48px 24px",
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              background: "#EFF6FF",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#2563EB",
            }}
          >
            <Icon.Target size={28} />
          </div>
          <h3 style={{ fontSize: "1.05rem", fontWeight: 700, color: "#0F172A", margin: 0 }}>
            {targets.length === 0 ? "No Tasks Added Yet" : "No Tasks Matching Filters"}
          </h3>
          <p style={{ fontSize: "0.82rem", color: "#64748B", margin: 0, maxWidth: 460, lineHeight: 1.5 }}>
            {targets.length === 0
              ? "You can add your own quarterly tasks and work deliverables using the button below. Once marked completed, your employer can review and provide an appraisal."
              : "Try adjusting your search keywords, quarter selection, or status filters to see your tasks."}
          </p>

          <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
            <button
              type="button"
              onClick={() => openTaskModal(null)}
              style={{
                padding: "8px 18px",
                borderRadius: 8,
                background: "#2563EB",
                color: "#fff",
                border: "none",
                fontSize: "0.82rem",
                fontWeight: 700,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
                boxShadow: "0 2px 6px rgba(37,99,235,0.25)",
              }}
            >
              <Icon.Plus size={14} color="#fff" /> Add Your First Task
            </button>
            {(searchQuery || selectedQuarter !== "ALL" || selectedStatus !== "ALL" || selectedWorkType !== "ALL") && (
              <button
                type="button"
                onClick={() => {
                  setSearchQuery("");
                  setSelectedQuarter("ALL");
                  setSelectedStatus("ALL");
                  setSelectedWorkType("ALL");
                }}
                style={{
                  padding: "8px 16px",
                  borderRadius: 8,
                  background: "#EFF6FF",
                  border: "1px solid #DBEAFE",
                  color: "#2563EB",
                  fontSize: "0.82rem",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Reset Filters
              </button>
            )}
          </div>
        </motion.div>
      ) : (
        /* ── TABLE VIEW ── */
        <div
          style={{
            background: "#fff",
            borderRadius: 14,
            border: "1px solid #E2EEFF",
            overflow: "hidden",
            boxShadow: "0 1px 4px rgba(37,99,235,0.04)",
          }}
        >
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.82rem", textAlign: "left" }}>
              <thead>
                <tr style={{ background: "#F8FAFC", borderBottom: "1px solid #E2E8F0" }}>
                  <th style={{ padding: "13px 16px", fontWeight: 700, color: "#475569", fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>Quarter</th>
                  <th style={{ padding: "13px 16px", fontWeight: 700, color: "#475569", fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>Task & KPI</th>
                  <th style={{ padding: "13px 16px", fontWeight: 700, color: "#475569", fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>Work Type & Source</th>
                  <th style={{ padding: "13px 16px", fontWeight: 700, color: "#475569", fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>Due Date</th>
                  <th style={{ padding: "13px 16px", fontWeight: 700, color: "#475569", fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>Status</th>
                  <th style={{ padding: "13px 16px", fontWeight: 700, color: "#475569", fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>Employer Appraisal</th>
                  <th style={{ padding: "13px 16px", fontWeight: 700, color: "#475569", fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.05em", textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTargets.map((t, idx) => {
                  const workTypeTheme = getWorkTypeTheme(t.workType || t.priority);
                  const statusTheme = STATUS_THEMES[t.status] || STATUS_THEMES.ASSIGNED;
                  const isReviewed = t.rating > 0 || t.status === "REVIEWED";
                  const isUpdating = updatingId === t.id;
                  const isSelfCreated = (t.assignedBy || "").toLowerCase().startsWith("self");

                  // Due Date alert
                  let dueBadge = null;
                  if (t.dueDate) {
                    const due = dayjs(t.dueDate);
                    const today = dayjs().startOf("day");
                    const daysLeft = due.diff(today, "day");
                    if (daysLeft < 0 && t.status !== "COMPLETED" && t.status !== "REVIEWED") {
                      dueBadge = <span style={{ background: "#FEF2F2", color: "#DC2626", padding: "1px 5px", borderRadius: 4, fontSize: "0.68rem", fontWeight: 700, marginLeft: 4 }}>Overdue</span>;
                    } else if (daysLeft >= 0 && daysLeft <= 5 && t.status !== "COMPLETED" && t.status !== "REVIEWED") {
                      dueBadge = <span style={{ background: "#FFF7ED", color: "#EA580C", padding: "1px 5px", borderRadius: 4, fontSize: "0.68rem", fontWeight: 700, marginLeft: 4 }}>{daysLeft === 0 ? "Today" : `${daysLeft}d left`}</span>;
                    }
                  }

                  return (
                    <tr
                      key={t.id}
                      style={{
                        borderBottom: "1px solid #F1F5F9",
                        background: isReviewed ? "rgba(245, 243, 255, 0.45)" : (idx % 2 === 0 ? "#fff" : "#FAFCFF"),
                        transition: "background 0.15s ease",
                      }}
                    >
                      {/* Quarter */}
                      <td style={{ padding: "14px 16px", fontWeight: 700, color: "#1E293B", whiteSpace: "nowrap" }}>
                        <span
                          style={{
                            padding: "3px 8px",
                            borderRadius: 6,
                            background: "#F1F5F9",
                            color: "#334155",
                            fontSize: "0.74rem",
                            fontWeight: 700,
                            border: "1px solid #E2E8F0",
                          }}
                        >
                          {t.quarter}
                        </span>
                      </td>

                      {/* Task & KPI */}
                      <td style={{ padding: "14px 16px", maxWidth: 300 }}>
                        <div style={{ fontWeight: 700, color: "#0F172A", marginBottom: 3, fontSize: "0.86rem" }}>
                          {t.title}
                        </div>
                        {t.kpi && (
                          <div
                            style={{
                              fontSize: "0.74rem",
                              color: "#2563EB",
                              fontWeight: 600,
                              background: "#EFF6FF",
                              border: "1px solid #DBEAFE",
                              borderRadius: 6,
                              padding: "2px 7px",
                              display: "inline-block",
                              marginTop: 2,
                            }}
                          >
                            🎯 KPI: {t.kpi}
                          </div>
                        )}
                        {t.description && (
                          <div style={{ fontSize: "0.74rem", color: "#64748B", marginTop: 4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 280 }}>
                            {t.description}
                          </div>
                        )}
                      </td>

                      {/* Work Type & Source */}
                      <td style={{ padding: "14px 16px", whiteSpace: "nowrap" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: 5, alignItems: "flex-start" }}>
                          <span
                            style={{
                              padding: "3px 8px",
                              borderRadius: 6,
                              background: workTypeTheme.bg,
                              color: workTypeTheme.text,
                              border: `1px solid ${workTypeTheme.border}`,
                              fontSize: "0.72rem",
                              fontWeight: 700,
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 5,
                            }}
                          >
                            <span style={{ width: 6, height: 6, borderRadius: "50%", background: workTypeTheme.dot }} />
                            {workTypeTheme.label}
                          </span>

                          <span
                            style={{
                              fontSize: "0.68rem",
                              fontWeight: 600,
                              padding: "2px 6px",
                              borderRadius: 4,
                              background: isSelfCreated ? "#FAF5FF" : "#F1F5F9",
                              color: isSelfCreated ? "#7C3AED" : "#64748B",
                              border: `1px solid ${isSelfCreated ? "#E9D5FF" : "#E2E8F0"}`,
                            }}
                          >
                            {isSelfCreated ? "👤 Self-Added" : "🏢 Manager Assigned"}
                          </span>
                        </div>
                      </td>

                      {/* Due Date */}
                      <td style={{ padding: "14px 16px", whiteSpace: "nowrap", color: "#475569" }}>
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <span>{t.dueDate ? dayjs(t.dueDate).format("MMM D, YYYY") : "—"}</span>
                          {dueBadge}
                        </div>
                      </td>

                      {/* Status */}
                      <td style={{ padding: "14px 16px", whiteSpace: "nowrap" }}>
                        <span
                          style={{
                            padding: "4px 11px",
                            borderRadius: 999,
                            background: statusTheme.bg,
                            color: statusTheme.text,
                            border: `1px solid ${statusTheme.border}`,
                            fontSize: "0.74rem",
                            fontWeight: 700,
                            display: "inline-block",
                          }}
                        >
                          {statusTheme.label}
                        </span>
                      </td>

                      {/* Employer Appraisal / Rating */}
                      <td style={{ padding: "14px 16px", minWidth: 170 }}>
                        {t.rating > 0 ? (
                          <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              {renderStarIcons(t.rating, 13)}
                              <span style={{ fontSize: "0.75rem", fontWeight: 700, color: "#6D28D9" }}>
                                {t.rating} / 5
                              </span>
                            </div>
                            <span style={{ fontSize: "0.7rem", color: "#7C3AED", fontWeight: 600 }}>
                              {RATING_DESCRIPTIONS[t.rating]?.split(" - ")[1] || "Appraised"}
                            </span>
                            {t.appraisalFeedback && (
                              <div style={{ fontSize: "0.72rem", color: "#475569", fontStyle: "italic", maxWidth: 180, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                "{t.appraisalFeedback}"
                              </div>
                            )}
                          </div>
                        ) : t.status === "COMPLETED" ? (
                          <span style={{ fontSize: "0.72rem", color: "#059669", background: "#ECFDF5", padding: "3px 8px", borderRadius: 6, border: "1px solid #A7F3D0", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4 }}>
                            <Icon.Clock size={11} color="#059669" /> Awaiting Appraisal
                          </span>
                        ) : (
                          <span style={{ fontSize: "0.74rem", color: "#94A3B8" }}>In progress</span>
                        )}
                      </td>

                      {/* Actions */}
                      <td style={{ padding: "14px 16px", textAlign: "right", whiteSpace: "nowrap" }}>
                        <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                          <button
                            type="button"
                            onClick={() => setDetailTarget(t)}
                            title="View Task Details & Appraisal"
                            style={{
                              padding: "5px 10px",
                              borderRadius: 7,
                              background: isReviewed ? "#FAF5FF" : "#EFF6FF",
                              border: `1px solid ${isReviewed ? "#DDD6FE" : "#DBEAFE"}`,
                              color: isReviewed ? "#7C3AED" : "#2563EB",
                              fontSize: "0.75rem",
                              fontWeight: 700,
                              cursor: "pointer",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                            }}
                          >
                            {isReviewed ? <><Icon.Star size={11} color="#7C3AED" /> View Appraisal</> : "Details"}
                          </button>

                          <button
                            type="button"
                            onClick={() => openTaskModal(t)}
                            title="Edit Task"
                            style={{
                              padding: "5px 8px",
                              borderRadius: 7,
                              background: "#F8FAFC",
                              border: "1px solid #E2E8F0",
                              color: "#475569",
                              fontSize: "0.75rem",
                              fontWeight: 600,
                              cursor: "pointer",
                            }}
                          >
                            <Icon.Edit size={12} color="#475569" />
                          </button>

                          <button
                            type="button"
                            onClick={() => setDeleteModal({ isOpen: true, targetId: t.id, title: t.title })}
                            title="Delete Task"
                            style={{
                              padding: "5px 8px",
                              borderRadius: 7,
                              background: "#FEF2F2",
                              border: "1px solid #FECACA",
                              color: "#DC2626",
                              fontSize: "0.75rem",
                              fontWeight: 600,
                              cursor: "pointer",
                            }}
                          >
                            <Icon.X size={12} color="#DC2626" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── ADD / EDIT TASK MODAL ── */}
      <AnimatePresence>
        {taskModal.isOpen && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(15, 23, 42, 0.45)",
              backdropFilter: "blur(3px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9999,
              padding: 16,
            }}
            onClick={() => setTaskModal({ isOpen: false, target: null })}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 16 }}
              transition={{ duration: 0.2 }}
              onClick={e => e.stopPropagation()}
              style={{
                background: "#fff",
                borderRadius: 16,
                maxWidth: 540,
                width: "100%",
                maxHeight: "90vh",
                boxShadow: "0 20px 35px -5px rgba(0,0,0,0.2)",
                border: "1px solid #E2EEFF",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  padding: "16px 22px",
                  borderBottom: "1px solid #E2EEFF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  background: "#F8FBFF",
                  flexShrink: 0,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 8,
                      background: "#EFF6FF",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#2563EB",
                    }}
                  >
                    <Icon.Target size={16} color="#2563EB" />
                  </div>
                  <h4 style={{ fontSize: "0.98rem", fontWeight: 700, color: "#0F172A", margin: 0 }}>
                    {taskModal.target ? "Edit Task / Target" : "Add New Task / Goal"}
                  </h4>
                </div>
                <button
                  type="button"
                  aria-label="Close"
                  onClick={() => setTaskModal({ isOpen: false, target: null })}
                  style={{ background: "none", border: "none", cursor: "pointer", color: "#94A3B8", fontSize: "1.1rem" }}
                >
                  ✕
                </button>
              </div>

              <form
                onSubmit={handleSaveTask}
                style={{
                  padding: "20px 22px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 14,
                  overflowY: "auto",
                  maxHeight: "calc(90vh - 65px)",
                }}
              >
                {/* Quarter & Work Type */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <label style={{ fontSize: "0.74rem", fontWeight: 700, color: "#475569", textTransform: "uppercase" }}>Quarter *</label>
                    <select
                      value={taskForm.quarter}
                      onChange={e => setTaskForm({ ...taskForm, quarter: e.target.value })}
                      style={{ padding: "8px 12px", borderRadius: 8, border: "1.5px solid #CBD5E1", fontSize: "0.84rem", fontWeight: 600, color: "#0F172A", outline: "none", background: "#fff" }}
                      required
                    >
                      {quarterSelectOptions.map(q => (
                        <option key={q.key} value={q.key}>{q.label}</option>
                      ))}
                    </select>
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <label style={{ fontSize: "0.74rem", fontWeight: 700, color: "#475569", textTransform: "uppercase" }}>Work Type *</label>
                    <select
                      value={taskForm.workType}
                      onChange={e => setTaskForm({ ...taskForm, workType: e.target.value, priority: e.target.value })}
                      style={{ padding: "8px 12px", borderRadius: 8, border: "1.5px solid #CBD5E1", fontSize: "0.84rem", fontWeight: 600, color: "#0F172A", outline: "none", background: "#fff" }}
                    >
                      <option value="BUSINESS_WORK">Business Work</option>
                      <option value="UPSKILLING">Upskilling</option>
                    </select>
                  </div>
                </div>

                {/* Progress Status Selector */}
                <div style={{ display: "flex", flexDirection: "column", gap: 6, background: "#F8FAFC", padding: "12px 14px", borderRadius: 10, border: "1px solid #E2E8F0" }}>
                  <label style={{ fontSize: "0.74rem", fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                    Update Your Progress Status:
                  </label>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {[
                      { key: "ASSIGNED", label: "Assigned", bg: "#EFF6FF", text: "#2563EB", border: "#2563EB" },
                      { key: "IN_PROGRESS", label: "In Progress", bg: "#FFFBEB", text: "#D97706", border: "#D97706" },
                      { key: "COMPLETED", label: "Completed", bg: "#ECFDF5", text: "#059669", border: "#059669" },
                    ].map(s => {
                      const isCurrent = (taskForm.status || "ASSIGNED").toUpperCase() === s.key;
                      return (
                        <button
                          key={s.key}
                          type="button"
                          onClick={() => setTaskForm(prev => ({ ...prev, status: s.key }))}
                          style={{
                            padding: "8px 18px",
                            borderRadius: 8,
                            border: isCurrent ? `2px solid ${s.border}` : "1.5px solid #CBD5E1",
                            background: isCurrent ? s.bg : "#fff",
                            color: isCurrent ? s.text : "#475569",
                            fontSize: "0.82rem",
                            fontWeight: isCurrent ? 800 : 600,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            boxShadow: isCurrent ? "0 1px 4px rgba(0,0,0,0.06)" : "none",
                            transition: "all 0.15s ease",
                          }}
                        >
                          {isCurrent && <span style={{ fontWeight: 800 }}>✓</span>}
                          <span>{s.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Dynamic Title / Deliverable */}
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <label style={{ fontSize: "0.74rem", fontWeight: 700, color: "#475569", textTransform: "uppercase" }}>
                    {(taskForm.workType || "").toUpperCase().includes("UPSKILL") ? "Project Title (Optional)" : "Target Title / Project *"}
                  </label>
                  <input
                    type="text"
                    placeholder={(taskForm.workType || "").toUpperCase().includes("UPSKILL")
                      ? "e.g. AWS Cloud Certification or Fullstack Project (Optional)"
                      : "e.g. Implement Payment Gateway Integration & Unit Tests"}
                    value={taskForm.title}
                    onChange={e => setTaskForm({ ...taskForm, title: e.target.value })}
                    style={{ padding: "8px 12px", borderRadius: 8, border: "1.5px solid #CBD5E1", fontSize: "0.84rem", outline: "none" }}
                    required={!(taskForm.workType || "").toUpperCase().includes("UPSKILL")}
                  />
                </div>

                {/* Target Due Date */}
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <label style={{ fontSize: "0.74rem", fontWeight: 700, color: "#475569", textTransform: "uppercase" }}>Target Due Date</label>
                  <input
                    type="date"
                    value={taskForm.dueDate}
                    onChange={e => setTaskForm({ ...taskForm, dueDate: e.target.value })}
                    style={{ padding: "8px 12px", borderRadius: 8, border: "1.5px solid #CBD5E1", fontSize: "0.84rem", outline: "none" }}
                  />
                </div>

                {/* Description / Scope / Skills */}
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <label style={{ fontSize: "0.74rem", fontWeight: 700, color: "#475569", textTransform: "uppercase" }}>
                    {(taskForm.workType || "").toUpperCase().includes("UPSKILL") ? "Description About What Skills You Are Learning *" : "Work Scope & Details"}
                  </label>
                  <textarea
                    rows={3}
                    placeholder={(taskForm.workType || "").toUpperCase().includes("UPSKILL")
                      ? "Describe the skills, technologies, concepts, courses, or certifications you are learning..."
                      : "Describe what you plan to accomplish, key tasks, and background notes..."}
                    value={taskForm.description}
                    onChange={e => setTaskForm({ ...taskForm, description: e.target.value })}
                    style={{ padding: "8px 12px", borderRadius: 8, border: "1.5px solid #CBD5E1", fontSize: "0.82rem", outline: "none", resize: "vertical", fontFamily: "inherit" }}
                  />
                </div>

                {/* Key Deliverables & KPIs (Only for Business Work) */}
                {!(taskForm.workType || "").toUpperCase().includes("UPSKILL") && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <label style={{ fontSize: "0.74rem", fontWeight: 700, color: "#475569", textTransform: "uppercase" }}>
                      Key Deliverables & Success Metric (KPI)
                    </label>
                    <textarea
                      rows={2}
                      placeholder="e.g. 1. Complete code coverage > 85%&#10;2. Deploy to staging by end of month"
                      value={taskForm.kpi}
                      onChange={e => setTaskForm({ ...taskForm, kpi: e.target.value })}
                      style={{ padding: "8px 12px", borderRadius: 8, border: "1.5px solid #CBD5E1", fontSize: "0.82rem", outline: "none", resize: "vertical", fontFamily: "inherit" }}
                    />
                  </div>
                )}

                {/* Notice banner */}
                <div style={{ padding: "8px 12px", background: "#F8FAFC", borderRadius: 8, border: "1px solid #E2E8F0", fontSize: "0.74rem", color: "#64748B" }}>
                  {(taskForm.workType || "").toUpperCase().includes("UPSKILL")
                    ? "💡 This upskilling goal will sync with the employer portal. Once you complete it, your employer can review and provide an appraisal."
                    : "💡 This task will immediately sync with the employer portal. After you complete the task, your employer can review and provide an appraisal."}
                </div>

                {/* Sticky Actions Footer */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 10,
                    marginTop: 6,
                    borderTop: "1px solid #F1F5F9",
                    paddingTop: 12,
                    paddingBottom: 2,
                    position: "sticky",
                    bottom: -20,
                    background: "#fff",
                    zIndex: 10,
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setTaskModal({ isOpen: false, target: null })}
                    style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #CBD5E1", background: "#fff", fontSize: "0.8rem", fontWeight: 600, color: "#475569", cursor: "pointer" }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    style={{
                      padding: "8px 20px",
                      borderRadius: 8,
                      border: "none",
                      background: "#2563EB",
                      fontSize: "0.82rem",
                      fontWeight: 700,
                      color: "#fff",
                      cursor: "pointer",
                      opacity: submitting ? 0.6 : 1,
                      boxShadow: "0 2px 6px rgba(37,99,235,0.25)",
                    }}
                  >
                    {submitting ? "Saving..." : (taskModal.target ? "Update Task" : "Add Task")}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ── DELETE CONFIRMATION MODAL ── */}
      <AnimatePresence>
        {deleteModal.isOpen && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(15, 23, 42, 0.45)",
              backdropFilter: "blur(3px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10000,
              padding: 16,
            }}
            onClick={() => setDeleteModal({ isOpen: false, targetId: null, title: "" })}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={e => e.stopPropagation()}
              style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 420, padding: "20px", boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1)" }}
            >
              <h4 style={{ fontSize: "0.95rem", fontWeight: 700, color: "#0F172A", margin: 0 }}>Delete Task?</h4>
              <p style={{ fontSize: "0.82rem", color: "#64748B", marginTop: 8 }}>
                Are you sure you want to delete the task <strong>"{deleteModal.title}"</strong>? This will remove it from your tasks list and the employer portal.
              </p>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
                <button
                  type="button"
                  onClick={() => setDeleteModal({ isOpen: false, targetId: null, title: "" })}
                  style={{ padding: "7px 14px", borderRadius: 8, border: "1px solid #CBD5E1", background: "#fff", fontSize: "0.8rem", fontWeight: 600, cursor: "pointer" }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteTask}
                  disabled={submitting}
                  style={{ padding: "7px 16px", borderRadius: 8, border: "none", background: "#DC2626", color: "#fff", fontSize: "0.8rem", fontWeight: 700, cursor: "pointer" }}
                >
                  {submitting ? "Deleting..." : "Delete Task"}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ── DETAIL & STATUS UPDATE MODAL ── */}
      <AnimatePresence>
        {detailTarget && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(15, 23, 42, 0.45)",
              backdropFilter: "blur(4px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9999,
              padding: 20,
            }}
            onClick={() => setDetailTarget(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 16 }}
              transition={{ duration: 0.2 }}
              onClick={e => e.stopPropagation()}
              style={{
                background: "#fff",
                borderRadius: 18,
                maxWidth: 580,
                width: "100%",
                maxHeight: "90vh",
                overflowY: "auto",
                boxShadow: "0 20px 40px rgba(0,0,0,0.18)",
                border: "1px solid #E2EEFF",
                padding: "26px 28px",
                display: "flex",
                flexDirection: "column",
                gap: 20,
              }}
            >
              {/* Modal Header */}
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <span
                      style={{
                        padding: "3px 9px",
                        borderRadius: 6,
                        background: "#EFF6FF",
                        color: "#2563EB",
                        fontSize: "0.72rem",
                        fontWeight: 700,
                      }}
                    >
                      {detailTarget.quarter}
                    </span>
                    {(() => {
                      const detailWorkTypeTheme = getWorkTypeTheme(detailTarget.workType || detailTarget.priority);
                      return (
                        <span
                          style={{
                            padding: "3px 8px",
                            borderRadius: 6,
                            background: detailWorkTypeTheme.bg,
                            color: detailWorkTypeTheme.text,
                            fontSize: "0.72rem",
                            fontWeight: 700,
                            border: `1px solid ${detailWorkTypeTheme.border}`,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 5,
                          }}
                        >
                          <span style={{ width: 6, height: 6, borderRadius: "50%", background: detailWorkTypeTheme.dot }} />
                          {detailWorkTypeTheme.label}
                        </span>
                      );
                    })()}
                    {(detailTarget.assignedBy || "").toLowerCase().startsWith("self") && (
                      <span
                        style={{
                          padding: "3px 8px",
                          borderRadius: 6,
                          background: "#FAF5FF",
                          color: "#7C3AED",
                          fontSize: "0.7rem",
                          fontWeight: 700,
                          border: "1px solid #E9D5FF",
                        }}
                      >
                        👤 Self-Added
                      </span>
                    )}
                  </div>
                  <h3 style={{ fontSize: "1.15rem", fontWeight: 800, color: "#0F172A", margin: 0, lineHeight: 1.35 }}>
                    {detailTarget.title}
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={() => setDetailTarget(null)}
                  style={{
                    background: "#F1F5F9",
                    border: "none",
                    borderRadius: "50%",
                    width: 28,
                    height: 28,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#64748B",
                    fontSize: "0.85rem",
                    fontWeight: 700,
                  }}
                >
                  ✕
                </button>
              </div>

              {/* Status & Assignment Quick Row */}
              <div
                style={{
                  background: "#F8FAFC",
                  borderRadius: 10,
                  padding: "12px 16px",
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
                  gap: 12,
                  fontSize: "0.76rem",
                }}
              >
                <div>
                  <div style={{ color: "#94A3B8", fontWeight: 600 }}>CURRENT STATUS</div>
                  <div style={{ marginTop: 2, fontWeight: 700, color: STATUS_THEMES[detailTarget.status]?.text || "#2563EB" }}>
                    {STATUS_THEMES[detailTarget.status]?.label || detailTarget.status}
                  </div>
                </div>
                <div>
                  <div style={{ color: "#94A3B8", fontWeight: 600 }}>DUE DATE</div>
                  <div style={{ marginTop: 2, fontWeight: 700, color: "#1E293B" }}>
                    {detailTarget.dueDate ? dayjs(detailTarget.dueDate).format("MMM D, YYYY") : "No due date"}
                  </div>
                </div>
                <div>
                  <div style={{ color: "#94A3B8", fontWeight: 600 }}>SOURCE / BY</div>
                  <div style={{ marginTop: 2, fontWeight: 700, color: "#1E293B" }}>
                    {(detailTarget.assignedBy || "").toLowerCase().startsWith("self") ? "Self (You)" : (detailTarget.assignedBy || "Manager")}
                  </div>
                </div>
                <div>
                  <div style={{ color: "#94A3B8", fontWeight: 600 }}>ADDED ON</div>
                  <div style={{ marginTop: 2, fontWeight: 700, color: "#1E293B" }}>
                    {detailTarget.assignedAt ? dayjs(detailTarget.assignedAt).format("MMM D, YYYY") : "—"}
                  </div>
                </div>
              </div>

              {/* Description / Skills */}
              {detailTarget.description && (
                <div>
                  <div style={{ fontSize: "0.75rem", fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 6 }}>
                    {(detailTarget.workType || detailTarget.priority || "").toUpperCase().includes("UPSKILL") ? "Skills Being Learned & Scope" : "Description & Objectives"}
                  </div>
                  <div style={{ fontSize: "0.84rem", color: "#334155", lineHeight: 1.5, whiteSpace: "pre-wrap", background: "#fff", padding: "12px 14px", borderRadius: 10, border: "1px solid #E2E8F0" }}>
                    {detailTarget.description}
                  </div>
                </div>
              )}

              {/* KPI / Deliverables */}
              {detailTarget.kpi && (
                <div>
                  <div style={{ fontSize: "0.75rem", fontWeight: 700, color: "#2563EB", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
                    <Icon.Target size={14} color="#2563EB" />
                    {(detailTarget.workType || detailTarget.priority || "").toUpperCase().includes("UPSKILL") ? "Learning Outcomes & Deliverables (KPI)" : "Key Deliverables & KPI"}
                  </div>
                  <div style={{ fontSize: "0.84rem", color: "#1E293B", lineHeight: 1.5, background: "#EFF6FF", padding: "12px 14px", borderRadius: 10, border: "1px solid #DBEAFE", fontWeight: 500 }}>
                    {detailTarget.kpi}
                  </div>
                </div>
              )}

              {/* Performance Appraisal Section (Read-Only Appraisal from Employer) */}
              {(detailTarget.rating > 0 || detailTarget.status === "REVIEWED" || detailTarget.appraisalFeedback) ? (
                <div
                  style={{
                    background: "linear-gradient(135deg, #FAF5FF 0%, #F5F3FF 100%)",
                    border: "1.5px solid #DDD6FE",
                    borderRadius: 14,
                    padding: "16px 18px",
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ color: "#7C3AED" }}><Icon.Award size={18} /></span>
                      <span style={{ fontSize: "0.82rem", fontWeight: 800, color: "#7C3AED", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                        Employer Performance Appraisal
                      </span>
                    </div>
                    {detailTarget.reviewedAt && (
                      <span style={{ fontSize: "0.72rem", color: "#8B5CF6" }}>
                        Reviewed on {dayjs(detailTarget.reviewedAt).format("MMMM D, YYYY")}
                      </span>
                    )}
                  </div>

                  {detailTarget.rating > 0 && (
                    <div style={{ display: "flex", alignItems: "center", gap: 10, background: "#fff", padding: "10px 14px", borderRadius: 10, border: "1px solid #EDE9FE" }}>
                      {renderStarIcons(detailTarget.rating, 18)}
                      <span style={{ fontSize: "0.9rem", fontWeight: 800, color: "#5B21B6" }}>
                        {RATING_DESCRIPTIONS[detailTarget.rating] || `${detailTarget.rating} / 5`}
                      </span>
                    </div>
                  )}

                  {detailTarget.appraisalFeedback && (
                    <div style={{ background: "#fff", padding: "12px 14px", borderRadius: 10, border: "1px solid #EDE9FE" }}>
                      <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "#6D28D9", marginBottom: 4 }}>
                        Manager Appraisal Feedback & Notes:
                      </div>
                      <p style={{ fontSize: "0.84rem", color: "#334155", fontStyle: "italic", margin: 0, lineHeight: 1.5 }}>
                        "{detailTarget.appraisalFeedback}"
                      </p>
                    </div>
                  )}
                </div>
              ) : detailTarget.status === "COMPLETED" ? (
                <div style={{ background: "#ECFDF5", border: "1px solid #A7F3D0", padding: "12px 16px", borderRadius: 10, display: "flex", alignItems: "center", gap: 10 }}>
                  <Icon.CheckCircle size={18} color="#059669" />
                  <div>
                    <div style={{ fontSize: "0.8rem", fontWeight: 700, color: "#065F46" }}>Task Completed!</div>
                    <div style={{ fontSize: "0.74rem", color: "#047857" }}>Your employer has been notified and will provide an appraisal review & rating soon.</div>
                  </div>
                </div>
              ) : null}

              {/* Modal Footer with Edit / Delete / Close */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid #F1F5F9", paddingTop: 16 }}>
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    type="button"
                    onClick={() => {
                      const tgt = detailTarget;
                      setDetailTarget(null);
                      openTaskModal(tgt);
                    }}
                    style={{
                      padding: "8px 14px",
                      borderRadius: 8,
                      border: "1px solid #CBD5E1",
                      background: "#fff",
                      color: "#334155",
                      fontSize: "0.8rem",
                      fontWeight: 600,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <Icon.Edit size={13} color="#334155" /> Edit Task
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      const tgt = detailTarget;
                      setDetailTarget(null);
                      setDeleteModal({ isOpen: true, targetId: tgt.id, title: tgt.title });
                    }}
                    style={{
                      padding: "8px 14px",
                      borderRadius: 8,
                      border: "1px solid #FECACA",
                      background: "#FEF2F2",
                      color: "#DC2626",
                      fontSize: "0.8rem",
                      fontWeight: 600,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <Icon.X size={13} color="#DC2626" /> Delete Task
                  </button>
                </div>

                <button
                  type="button"
                  onClick={() => setDetailTarget(null)}
                  style={{
                    padding: "8px 18px",
                    borderRadius: 8,
                    background: "#F1F5F9",
                    color: "#475569",
                    border: "none",
                    fontSize: "0.82rem",
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  Close
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default EmployeeTargetsAppraisalsView;
